import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineView } from "foldkit/submodel";

import type { TagUsage } from "../../api/schemas/tags";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import * as Icons from "../../widgets/icons";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

const formatNumber = (value: number): string =>
	value.toLocaleString(undefined, { maximumFractionDigits: 0 });

/** v1 `usageWidth`: minimum 6%, capped at 100%, 0% when there is nothing. */
export const usageWidth = (value: number, total: number): string => {
	if (total <= 0) {
		return "0%";
	}
	const percent = Math.max(6, Math.round((value / total) * 100));
	return `${Math.min(percent, 100)}%`;
};

/** v1: `#tag`, quoted when the tag contains a space. */
export const tagQuery = (tag: string): string =>
	tag.includes(" ") ? `#"${tag}"` : `#${tag}`;

const statsCard = <Msg>(
	h: HtmlBuilder<Msg>,
	card: Readonly<{
		label: string;
		value: string;
		accent?: string;
		subtext: string;
	}>,
): Html =>
	h.div(
		[h.Class("rounded-2xl border border-base-300 bg-base-100 p-4 shadow-sm")],
		[
			h.div(
				[h.Class("text-sm font-semibold text-base-content/70")],
				[card.label],
			),
			h.div(
				[
					h.Class(
						"mt-1 flex items-baseline gap-2 text-3xl font-bold text-base-content",
					),
				],
				[
					card.value,
					card.accent === undefined
						? h.empty
						: h.span(
								[
									h.Class(
										"rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary",
									),
								],
								[card.accent],
							),
				],
			),
			h.div([h.Class("mt-1 text-sm text-base-content/70")], [card.subtext]),
		],
	);

const tagRow = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	tag: TagUsage,
	overallTotal: number,
): Html => {
	const percent =
		overallTotal > 0
			? `${Math.round((tag.total / overallTotal) * 100)}%`
			: "0%";
	// v1 links to `/?q=#tag`; the Searched page reads `q` from the route.
	const href = linkHref(
		shared.route,
		Route.Home({ asOf: Option.none(), q: Option.some(tagQuery(tag.tag)) }),
	);
	return h.tr(
		// v1 keys rows by lowercase tag, so tags differing only by case collide.
		[h.Key(tag.tag.toLowerCase())],
		[
			h.td(
				[],
				[
					h.div(
						[h.Class("flex items-center gap-2")],
						[
							h.a(
								[
									h.Href(href),
									h.Class(
										"badge badge-outline badge-sm hover:bg-primary hover:text-primary-content transition-colors",
									),
								],
								[tag.tag],
							),
							h.span(
								[h.Class("text-xs text-base-content/60")],
								[i18next.t("tags.percentOfAll", { percent })],
							),
						],
					),
				],
			),
			h.td(
				[h.Class("w-40")],
				[
					h.div(
						[h.Class("h-2 rounded-full bg-base-200")],
						[
							h.div(
								[
									h.Class("h-full rounded-full bg-primary"),
									h.Style({ width: usageWidth(tag.total, overallTotal) }),
									h.DataAttribute("testid", "share-bar"),
								],
								[],
							),
						],
					),
				],
			),
			h.td(
				[h.Class("text-right font-semibold text-base-content")],
				[formatNumber(tag.total)],
			),
			h.td(
				[h.Class("text-right text-base-content/80")],
				[formatNumber(tag.nodeCount)],
			),
			h.td(
				[h.Class("text-right text-base-content/80")],
				[formatNumber(tag.knotCount)],
			),
		],
	);
};

const content = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	tags: ReadonlyArray<TagUsage>,
	total: number,
): Html => {
	if (AsyncData.isFailure(model.tags)) {
		return h.div(
			[h.Role("alert"), h.Class("alert alert-error")],
			[
				Icons.AlertCircle(h, { class: "h-6 w-6 shrink-0 stroke-current" }),
				h.span([], [i18next.t("tags.loadError")]),
			],
		);
	}
	if (AsyncData.isLoading(model.tags)) {
		return h.div(
			[
				h.Role("status"),
				h.Class("flex items-center justify-center py-8 text-base-content/70"),
			],
			[
				Icons.Loader2(h, { size: 20, class: "mr-2 animate-spin" }),
				h.span([], [i18next.t("tags.loading")]),
			],
		);
	}
	if (tags.length === 0) {
		return h.div(
			[
				h.Class(
					"rounded-xl border border-dashed border-base-300 bg-base-200/70 p-6 text-center text-base-content/70",
				),
			],
			[
				h.p(
					[h.Class("font-semibold text-base-content")],
					[i18next.t("tags.noTagsTitle")],
				),
				h.p([h.Class("mt-1 text-sm")], [i18next.t("tags.noTagsDescription")]),
			],
		);
	}
	return h.div(
		[h.Class("overflow-x-auto")],
		[
			h.table(
				[h.Class("table")],
				[
					h.thead(
						[],
						[
							h.tr(
								[],
								[
									h.th([], [i18next.t("tags.tableTag")]),
									h.th(
										[h.Style({ width: "10rem" })],
										[i18next.t("tags.tableShare")],
									),
									h.th([h.Class("text-right")], [i18next.t("tags.tableTotal")]),
									h.th(
										[h.Class("text-right")],
										[i18next.t("tags.tableRecords")],
									),
									h.th(
										[h.Class("text-right")],
										[i18next.t("tags.tableEntries")],
									),
								],
							),
						],
					),
					h.tbody(
						[],
						tags.map((tag) => tagRow(h, shared, tag, total)),
					),
				],
			),
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const tags = AsyncData.getOrElse(model.tags, () => ({ data: [] })).data;
	const totals = tags.reduce(
		(acc, tag) => ({
			total: acc.total + tag.total,
			records: acc.records + tag.nodeCount,
			entries: acc.entries + tag.knotCount,
		}),
		{ total: 0, records: 0, entries: 0 },
	);
	const topTag = tags[0];
	const busy =
		AsyncData.isLoading(model.tags) || AsyncData.isRefreshing(model.tags);
	return h.div(
		[
			h.DataAttribute("testid", "page-Tags"),
			h.Class("mx-auto flex max-w-5xl flex-col gap-6"),
		],
		[
			h.header(
				[h.Class("flex flex-wrap items-start gap-4")],
				[
					h.div(
						[
							h.Class(
								"rounded-2xl bg-primary p-3 text-primary-content shadow-xl",
							),
						],
						[Icons.Hash(h, { size: 28 })],
					),
					h.div(
						[h.Class("flex-1 min-w-[18rem]")],
						[
							h.h1(
								[h.Class("text-3xl font-bold text-base-content")],
								[i18next.t("tags.title")],
							),
							h.p(
								[h.Class("mt-1 text-base-content/70")],
								[i18next.t("tags.description")],
							),
						],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-outline btn-sm"),
							h.AriaLabel(i18next.t("tags.refresh")),
							...busyAttributes(h, busy),
							h.OnClick(Message.ClickedRefresh()),
						],
						[
							busy ? busySpinner(h, busy) : Icons.RefreshCcw(h, { size: 16 }),
							i18next.t("tags.refresh"),
						],
					),
				],
			),
			h.div(
				[h.Class("grid gap-4 md:grid-cols-3")],
				[
					statsCard(h, {
						label: i18next.t("tags.uniqueTags"),
						value: formatNumber(tags.length),
						subtext: i18next.t("tags.uniqueTagsSubtext"),
					}),
					statsCard(h, {
						label: i18next.t("tags.totalAssignments"),
						value: formatNumber(totals.total),
						subtext: i18next.t("tags.assignmentBreakdown", {
							records: formatNumber(totals.records),
							entries: formatNumber(totals.entries),
						}),
					}),
					statsCard(h, {
						label: i18next.t("tags.mostUsed"),
						value: topTag ? topTag.tag : "—",
						...(topTag
							? { accent: i18next.t("tags.usesCount", { count: topTag.total }) }
							: {}),
						subtext: topTag
							? i18next.t("tags.topTagBreakdown", {
									records: formatNumber(topTag.nodeCount),
									entries: formatNumber(topTag.knotCount),
								})
							: i18next.t("tags.noTagsYet"),
					}),
				],
			),
			h.div(
				[h.Class("card border border-base-300 bg-base-100 shadow-sm")],
				[
					h.div(
						[h.Class("card-body gap-4")],
						[
							h.div(
								[h.Class("flex flex-wrap items-center justify-between gap-3")],
								[
									h.div(
										[],
										[
											h.div(
												[
													h.Class(
														"flex items-center gap-2 text-base font-semibold text-base-content",
													),
												],
												[
													Icons.Sparkles(h, {
														size: 16,
														class: "text-primary",
													}),
													h.span([], [i18next.t("tags.allTags")]),
												],
											),
											h.p(
												[h.Class("text-sm text-base-content/70")],
												[i18next.t("tags.usageCountsDescription")],
											),
										],
									),
									h.div(
										[
											h.Class("text-sm text-base-content/60"),
											h.DataAttribute("testid", "tags-status"),
										],
										[
											AsyncData.isRefreshing(model.tags)
												? i18next.t("tags.refreshing")
												: // v1 renders the render-time clock here (not live-ticking).
													i18next.t("tags.updated", {
														time: new Date().toLocaleTimeString(),
													}),
										],
									),
								],
							),
							content(h, model, shared, tags, totals.total),
						],
					),
				],
			),
		],
	);
});
